import { useEffect, useMemo, useState } from 'react'
import { AlarmClock, Bell, CalendarClock, FileText, MessageSquare, Pause, Pencil, Play, Plus, Sparkles, Trash2, X } from 'lucide-react'
import type { SchedulePreview, Task, TaskInput, TaskLimits, TaskRun } from '@shared/types'
import { useStore } from '../../store'
import { api } from '../../lib/api'
import { cn, relativeTime } from '../../lib/utils'
import { ViewHeader } from '../shell/ViewHeader'
import { otherTz, runText, untilText, useNow } from './schedule-ui'

type Filter = 'all' | 'chat' | 'report' | 'remind' | 'paused' | 'done'
const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'chat', label: 'In chats' },
  { value: 'report', label: 'Reports' },
  { value: 'remind', label: 'Reminders' },
  { value: 'paused', label: 'Paused' },
  { value: 'done', label: 'Finished' },
]

const TEMPLATES: Partial<TaskInput>[] = [
  {
    title: 'Daily pre-market brief',
    schedule: { kind: 'market', event: 'open', offsetMin: -60 },
    runIn: 'new',
    notify: 'both',
    instructions:
      'Give me a concise pre-market brief: overnight futures, key economic events today, and any major news on my portfolio holdings and watchlist. Flag anything that could move my positions. Use live data and web/X research.',
  },
  {
    title: 'Weekly market wrap',
    schedule: { kind: 'weekly', weekdays: [5], times: ['16:30'] },
    runIn: 'new',
    notify: 'both',
    instructions:
      'Summarize the most important US market moves of the past week — major index moves, notable earnings, and any names in my watchlist/portfolio. End with one thing to watch next week. Keep it tight and skimmable.',
  },
  {
    title: 'Fear-selling scan after the close',
    schedule: { kind: 'market', event: 'close', offsetMin: 20 },
    runIn: 'new',
    notify: 'both',
    instructions:
      'Find US large caps that fell hard today (≥ 5%) and judge each drop: justified by fundamentals/news, or fear-selling that overshoots? Rank the best 3 overreactions with the evidence.',
  },
]

const status = (t: Task) => (!t.enabled ? 'paused' : t.doneAt ? 'done' : 'active')

export function TaskIcon({ t, size = 15 }: { t: Pick<Task, 'action' | 'runIn'>; size?: number }) {
  const I = t.action === 'remind' ? Bell : t.runIn === 'chat' ? MessageSquare : FileText
  return <I size={size} />
}

export function TasksPage() {
  const tasks = useStore((s) => s.tasks)
  const loadTasks = useStore((s) => s.loadTasks)
  const openEditor = useStore((s) => s.openTaskEditor)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [filter, setFilter] = useState<Filter>('all')
  const [limits, setLimits] = useState<TaskLimits | null>(null)
  const now = useNow()

  useEffect(() => {
    void loadTasks()
  }, [loadTasks])
  useEffect(() => {
    api.taskLimits().then(setLimits).catch(() => {})
  }, [tasks])

  const shown = useMemo(() => {
    const list = tasks.filter((t) => {
      const s = status(t)
      switch (filter) {
        case 'chat':
          return t.runIn === 'chat' && t.action === 'ai'
        case 'report':
          return t.runIn === 'new' && t.action === 'ai'
        case 'remind':
          return t.action === 'remind'
        case 'paused':
          return s === 'paused'
        case 'done':
          return s === 'done'
        default:
          return true
      }
    })
    // Active first, soonest next run first; then paused; finished last.
    const rank = (t: Task) => (status(t) === 'active' ? 0 : status(t) === 'paused' ? 1 : 2)
    return list.sort((a, b) => rank(a) - rank(b) || (a.nextRunAt ?? Infinity) - (b.nextRunAt ?? Infinity) || b.createdAt - a.createdAt)
  }, [tasks, filter])
  const selected = tasks.find((t) => t.id === selectedId) ?? null

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col overflow-hidden bg-[var(--color-base)]">
      <ViewHeader title="Tasks" />
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-4 sm:px-6">
        <div>
          <h1 className="text-[20px] font-semibold text-fg">Tasks</h1>
          {limits && (
            <div className="num mt-0.5 text-[11.5px] text-fg-subtle" title="AI runs no one typed today: scheduled tasks, watchers, scans, worker callbacks">
              AI wakeups today {limits.wakes.used} / {limits.wakes.max}
            </div>
          )}
        </div>
        <button onClick={() => openEditor({ preset: {} })} className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-accent)] px-3 py-2 text-[12.5px] font-semibold text-[var(--color-base)]">
          <Plus size={15} /> New task
        </button>
      </div>

      <div className="grid min-h-0 flex-1 lg:grid-cols-[minmax(0,480px)_1fr]">
        <div className="min-h-0 overflow-y-auto border-b border-[var(--line-soft)] p-4 sm:px-6 lg:border-b-0 lg:border-r">
          {tasks.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-1">
              {FILTERS.map((f) => (
                <button
                  key={f.value}
                  onClick={() => setFilter(f.value)}
                  className={cn('rounded-md px-2 py-1 text-[11.5px] transition-colors', filter === f.value ? 'bg-[var(--color-surface-3)] text-fg' : 'text-fg-subtle hover:text-fg')}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
          {tasks.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="space-y-1.5">
              {shown.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setSelectedId(t.id)}
                  className={cn(
                    'flex w-full items-center justify-between gap-3 rounded-xl border px-3.5 py-2.5 text-left transition-colors',
                    selectedId === t.id ? 'border-[var(--line)] bg-[var(--color-surface-2)]' : 'border-[var(--line-soft)] hover:bg-[var(--color-surface-2)]',
                  )}
                >
                  <span className="flex min-w-0 items-center gap-2.5">
                    <span className={cn('shrink-0', status(t) === 'active' ? 'text-fg-muted' : 'text-fg-faint')}>
                      <TaskIcon t={t} />
                    </span>
                    <span className="min-w-0">
                      <span className="flex items-center gap-1.5">
                        <span className="truncate text-[13.5px] font-medium text-fg">{t.title}</span>
                        {t.createdBy === 'ai' && <Sparkles size={11} className="shrink-0 text-fg-faint" aria-label="made by the AI" />}
                      </span>
                      <span className="block truncate text-[11.5px] text-fg-subtle">
                        {t.scheduleText}
                        {t.runIn === 'chat' && t.chatTitle ? ` · ${t.chatTitle}` : ''}
                      </span>
                    </span>
                  </span>
                  <span className="num shrink-0 text-right text-[11px] text-fg-faint">
                    {status(t) === 'paused' ? 'Paused' : status(t) === 'done' ? 'Finished' : t.nextRunAt ? untilText(t.nextRunAt, now) : '—'}
                  </span>
                </button>
              ))}
              {!shown.length && <div className="px-1 py-6 text-center text-[12px] text-fg-faint">Nothing here.</div>}
            </div>
          )}

          <div className="mt-6">
            <div className="mb-2 text-[10px] uppercase tracking-wide text-fg-faint">Templates</div>
            <div className="grid gap-2 sm:grid-cols-2">
              {TEMPLATES.map((tpl) => (
                <button key={tpl.title} onClick={() => openEditor({ preset: tpl })} className="rounded-xl border border-[var(--line-soft)] p-3 text-left transition-colors hover:bg-[var(--color-surface-2)]">
                  <div className="flex items-center gap-1.5 text-[12.5px] font-medium text-fg">
                    <CalendarClock size={13} /> {tpl.title}
                  </div>
                  <div className="mt-1 line-clamp-2 text-[11px] text-fg-subtle">{tpl.instructions}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="hidden min-h-0 overflow-y-auto lg:block">
          {selected ? (
            <TaskDetail key={selected.id} task={selected} onClose={() => setSelectedId(null)} />
          ) : (
            <div className="flex h-full items-center justify-center px-6 text-center text-[13px] text-fg-faint">Select a task to see its schedule, next runs and history.</div>
          )}
        </div>
      </div>
      {/* Small screens: the detail as a sheet. */}
      {selected && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-[var(--color-base)] lg:hidden">
          <TaskDetail key={selected.id} task={selected} onClose={() => setSelectedId(null)} />
        </div>
      )}
    </div>
  )
}

function EmptyState() {
  const openEditor = useStore((s) => s.openTaskEditor)
  return (
    <div className="card flex flex-col items-center gap-2 px-6 py-10 text-center">
      <AlarmClock size={26} className="text-fg-subtle" />
      <div className="text-[14px] font-semibold text-fg">Nothing scheduled yet</div>
      <p className="max-w-sm text-[12.5px] text-fg-subtle">
        Anything from “in 30 seconds” to “every year on Mar 15” — an AI check inside one of your chats, a standalone report, or a plain reminder. In a chat you can also just ask: “check PAYX at 09:25 Monday”.
      </p>
      <button onClick={() => openEditor({ preset: {} })} className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-[var(--line)] bg-[var(--color-surface-2)] px-3 py-2 text-[12.5px] font-medium text-fg">
        <Plus size={14} /> New task
      </button>
    </div>
  )
}

const RUN_DOT: Record<TaskRun['status'], string> = { ok: 'bg-up', quiet: 'bg-[var(--color-fg-faint)]', skipped: 'bg-[var(--color-fg-subtle)]', error: 'bg-down' }

export function TaskDetail({ task, onClose }: { task: Task; onClose: () => void }) {
  const toggleTask = useStore((s) => s.toggleTask)
  const removeTask = useStore((s) => s.removeTask)
  const runTaskNow = useStore((s) => s.runTaskNow)
  const selectChat = useStore((s) => s.selectChat)
  const openEditor = useStore((s) => s.openTaskEditor)
  const [runs, setRuns] = useState<TaskRun[] | null>(null)
  const [next, setNext] = useState<SchedulePreview | null>(null)
  const [testing, setTesting] = useState(false)
  const now = useNow()
  const tz = task.schedule.tz

  const loadRuns = () => api.taskRuns(task.id).then(setRuns).catch(() => setRuns([]))
  useEffect(() => {
    setRuns(null)
    void loadRuns()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id, task.lastRunAt, task.runCount])
  useEffect(() => {
    if (!task.enabled || task.doneAt) return setNext(null)
    api.taskPreview({ schedule: task.schedule, created: task.createdAt, runs: task.runCount }).then(setNext).catch(() => {})
  }, [task.id, task.enabled, task.doneAt, task.runCount, task.schedule, task.createdAt])

  const st = status(task)
  return (
    <div className="p-5 sm:p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-[17px] font-semibold text-fg">
            <span className="text-fg-muted">
              <TaskIcon t={task} size={16} />
            </span>
            <span className="truncate">{task.title}</span>
          </h2>
          <div className="mt-0.5 text-[12.5px] text-fg-subtle">{task.scheduleText}</div>
          <div className="num mt-1 text-[12px] text-fg-muted">
            {st === 'paused' ? 'Paused' : st === 'done' ? `Finished ${task.doneAt ? relativeTime(task.doneAt) + ' ago' : ''}` : task.nextRunAt ? `Next: ${runText(task.nextRunAt, tz)} · ${untilText(task.nextRunAt, now)}` : '—'}
            {task.runCount ? <span className="text-fg-faint"> · {task.runCount} run{task.runCount === 1 ? '' : 's'}</span> : null}
          </div>
        </div>
        <button onClick={onClose} title="Close" className="rounded-md p-1 text-fg-subtle hover:text-fg">
          <X size={16} />
        </button>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {task.runIn === 'chat' && task.chatId && (
          <button onClick={() => void selectChat(task.chatId!)} className="inline-flex max-w-[260px] items-center gap-1.5 rounded-lg border border-[var(--line-soft)] px-2.5 py-1.5 text-[12px] text-fg-muted hover:text-fg">
            <MessageSquare size={13} /> <span className="truncate">{task.chatTitle ?? 'Open chat'}</span>
          </button>
        )}
        <button onClick={() => openEditor({ task })} className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--line-soft)] px-2.5 py-1.5 text-[12px] text-fg-muted hover:text-fg">
          <Pencil size={13} /> Edit
        </button>
        {st !== 'done' && (
          <button onClick={() => void toggleTask(task.id, !task.enabled)} className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--line-soft)] px-2.5 py-1.5 text-[12px] text-fg-muted hover:text-fg">
            {task.enabled ? <Pause size={13} /> : <Play size={13} />} {task.enabled ? 'Pause' : 'Resume'}
          </button>
        )}
        <button
          onClick={async () => {
            setTesting(true)
            await runTaskNow(task.id)
            await loadRuns()
            setTesting(false)
          }}
          disabled={testing}
          className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--line-soft)] px-2.5 py-1.5 text-[12px] text-fg-muted hover:text-fg disabled:opacity-50"
        >
          <Play size={13} /> {testing ? 'Running…' : 'Run now'}
        </button>
        <button
          onClick={() => {
            if (confirm(`Delete task "${task.title}"?`)) void removeTask(task.id)
          }}
          className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12px] text-fg-subtle hover:text-down"
        >
          <Trash2 size={13} /> Delete
        </button>
      </div>

      <div className="mt-4 grid gap-1 text-[11.5px] text-fg-subtle sm:grid-cols-2">
        <div>Does: {task.action === 'remind' ? 'a reminder (no AI)' : task.runIn === 'chat' ? 'an AI check inside its chat' : 'an AI report in a new chat'}</div>
        <div>Notify: {task.notify === 'auto' ? 'the AI decides' : task.notify === 'both' ? 'app + Telegram' : task.notify}</div>
        {task.action === 'ai' && <div>Mode: {task.mode ?? (task.runIn === 'chat' ? 'the chat’s' : 'expert')}{task.model ? ` · ${task.model}` : ''}</div>}
        <div>Made by: {task.createdBy === 'ai' ? 'the AI, in a chat' : 'you'}</div>
      </div>

      <p className="mt-4 whitespace-pre-wrap rounded-lg bg-[var(--color-inset)] p-3 text-[12.5px] leading-relaxed text-fg-muted">{task.instructions}</p>

      {next?.ok && next.runs && next.runs.length > 1 && (
        <div className="mt-5">
          <div className="mb-1.5 text-[11px] uppercase tracking-wide text-fg-subtle">Upcoming</div>
          <div className="space-y-0.5">
            {next.runs.map((r) => (
              <div key={r} className="num flex justify-between gap-3 text-[11.5px] text-fg-subtle">
                <span>
                  {runText(r, tz)} <span className="text-fg-faint">· {runText(r, otherTz(tz)).split(' ').slice(3).join(' ')}</span>
                </span>
                <span className="text-fg-faint">{untilText(r, now)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6">
        <div className="mb-2 text-[11px] uppercase tracking-wide text-fg-subtle">Run history</div>
        {runs === null ? (
          <div className="text-[12px] text-fg-faint">Loading…</div>
        ) : runs.length === 0 ? (
          <div className="rounded-lg border border-dashed border-[var(--line-soft)] px-4 py-8 text-center text-[12.5px] text-fg-faint">
            No runs yet. <b>Run now</b> tries it.
          </div>
        ) : (
          <div className="space-y-1.5">
            {runs.map((r) => (
              <button
                key={r.id}
                onClick={() => r.chatId && void selectChat(r.chatId)}
                disabled={!r.chatId}
                className="flex w-full items-start gap-2.5 rounded-lg border border-[var(--line-soft)] px-3 py-2 text-left transition-colors hover:bg-[var(--color-surface-2)] disabled:cursor-default"
              >
                <span className={cn('mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full', RUN_DOT[r.status])} />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center justify-between gap-2">
                    <span className="text-[11px] text-fg-subtle">{relativeTime(r.createdAt)}</span>
                    {r.status !== 'ok' && <span className="text-[10px] uppercase text-fg-faint">{r.status}</span>}
                  </span>
                  <span className="mt-0.5 line-clamp-2 text-[12px] text-fg-muted">{r.summary}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
